<template>
  <div>
    <template v-if="prefetchMod !== 'prefetchPage'">
      <div class="calc-page_title">{{ texts.calcTitle }}</div>
      <div class="calc-page_list-wrap">
        <ul class="calc-page_list">
          <li>
            <span class="calc-page_list-text">{{ texts.makeListItem1 }}</span>
          </li>
          <li>
            <span class="calc-page_list-text">{{ texts.makeListItem2 }}</span>
          </li>
          <li>
            <span class="calc-page_list-text">{{ texts.makeListItem3 }}</span>
          </li>
        </ul>
      </div>

      <div class="delivery-abroad">
        <a
          href="/business/export-online-stores/"
          class="delivery-abroad__link"
          >{{ texts.linkDeliveryAbroad }}</a
        >
      </div>

      <corpClientTips />
    </template>

    <div class="preparaion calc-page_form site-form">
      <div class="site-form_group">
        <div class="site-form_group_item">
          <div class="site-form_group_label">{{ texts.pickupFrom }}</div>
          <select-location
            ref="locationFrom"
            v-model="locationFrom"
            :isShipper="true"
            :defaultCountry="'RU'"
            :isLocationFrom="true"
          ></select-location>
        </div>
        <div class="site-form_group_item">
          <div class="site-form_group_label">{{ texts.deliveryTo }}</div>
          <select-location
            ref="locationTo"
            v-model="locationTo"
            :countryErrorMessage="countryToErrorMessage"
          ></select-location>
        </div>
      </div>

      <a
        :href="hrefPrefetchPage"
        v-if="prefetchMod === 'prefetchPage'"
        class="button-icon full calc-page__link-main-calc"
      >
        <i class="icon-check"></i>
        <span>{{ texts.calculate }}</span>
      </a>
      <template v-else>
        <pieces v-if="destinationsValid"></pieces>
        <div class="calculate-block" v-if="canCalculate">
          <button @click.prevent="calculatePrices" class="button-icon full">
            <i class="icon-check"></i>
            <span>{{ texts.calculateRates }}</span>
          </button>
          <div id="result-block">
            <calculate-results
              v-if="calculated"
              ref="resultBlock"
              :isHighlightingWalk="isHighlightingWalk"
              :load="isCalculated"
            ></calculate-results>
            <spinner class="spinner" v-if="isCalculated"></spinner>
          </div>
        </div>
      </template>
    </div>

    <async-modal ref="info">
      <span>{{ texts.infoMessage }} <a href="tel:88007000345" class="link">8 (800) 7000-345</a></span>
    </async-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { spinner } from '@/components/core';
import UrlBrowser from '@/helpers/UrlBrowser';
import selectLocation from './components/selectLocation/selectLocation.component.vue';
import pieces from './components/pieces/pieces.component.vue';
import corpClientTips from './components/corpClientTips/CorpClientTips.vue';
import asyncModal from '@/components/asyncModal/asyncModal.component.vue';

export default {
  name: 'preparation',
  components: {
    selectLocation,
    pieces,
    spinner,
    corpClientTips,
    'calculate-results': () =>
      import('./components/calculateResults/calculateResults.component.vue'),
    asyncModal,
  },
  data() {
    return {
      countryToErrorMessage: '',
      isCalculated: false,
      storeHandlers: null,
      prefetchMod: null,
      isHighlightingWalk: false,
    };
  },
  mounted() {
    this.storeHandlers = this.$store.subscribe(({ type }) => {
      switch (type) {
        case 'preparation/setLocationFrom':
        case 'preparation/setLocationTo':
        case 'preparation/addPiece':
        case 'preparation/editPiece':
        case 'preparation/removePiece':
          if (this.calculated) {
            this.resetCalculation();
          }
          break;
        default:
      }
    });

    const promisePrefetch = this.$prefetchCity.init();
    const { prefetchMod } = this.$prefetchCity;
    if (prefetchMod === 'prefetchPage') {
      this.prefetchMod = 'prefetchPage';
    }
    if (prefetchMod === 'prefetchPage' || prefetchMod === 'prefetchUrl') {
      this.$emit('root-load', true);
    }
    promisePrefetch.then(() => {
      this.$emit('root-load', false);
    });

    const urlBrowser = new UrlBrowser();
    const stepContinue = urlBrowser.getParamURLBrowser('step-one-continue');
    const highlightingWalk = urlBrowser.getParamURLBrowser('highlighting-walk');
    if (highlightingWalk === 'y') {
      this.isHighlightingWalk = true;
    }
    if (this.canCalculate && stepContinue === 'y') {
      this.calculatePrices();
    }
  },
  beforeDestroy() {
    this.storeHandlers();
  },
  watch: {
    destinationsValid() {
      if (!this.destinationsValid) {
        this.$store.dispatch('preparation/clearDestinations');
      }
    },
  },
  computed: {
    ...mapState('api', ['language']),
    ...mapState('preparation', [
      'pieces',
      'calculated',
      'locationFrom',
      'locationTo',
    ]),
    ...mapGetters('preparation', ['canCalculate']),
    locationFrom: {
      get() {
        return this.$store.state.preparation.locationFrom;
      },
      set(newLocation) {
        this.$store.dispatch('preparation/setLocationFrom', newLocation);
        this.countryToCheckError();
      },
    },
    locationTo: {
      get() {
        return this.$store.state.preparation.locationTo;
      },
      set(newLocation) {
        this.$store.dispatch('preparation/setLocationTo', newLocation);
        this.countryToCheckError();
      },
    },
    destinationsValid() {
      return (
        !!this.locationFrom &&
        (this.locationFrom.PostalCode || this.locationFrom.autoPostal) &&
        !!this.locationTo &&
        !this.countryToErrorMessage
      );
    },
    texts() {
      return {
        calcTitle: this.$t('CalcTitle'),
        CountryToRUErrorMessage: this.$t('CountryToRUErrorMessage'),
        CountryToUAErrorMessage: this.$t('CountryToUAErrorMessage'),
        CountryToBYErrorMessage: this.$t('CountryToBYErrorMessage'),
        makeListItem1: this.$t('MakeListItem1'),
        makeListItem2: this.$t('MakeListItem2'),
        makeListItem3: this.$t('MakeListItem3'),
        deliveryTo: this.$t('DeliveryTo'),
        pickupFrom: this.$t('PickupFrom'),
        calculate: this.$t('Calculate'),
        calculateRates: this.$t('CalculateRates'),
        linkDeliveryAbroad: this.$t('LinkDeliveryAbroad'),
        infoMessage: this.$t('InfoMessage'),
      };
    },
    hrefPrefetchPage() {
      const languagePath = this.language === 'en' ? '/en' : '';
      const locationArr = [];
      locationArr.push('url_prefetch_city=enable');
      locationArr.push('search_mode=zip');
      if (this.locationFrom && this.locationFrom.CityAlias) {
        const lf = encodeURIComponent(this.locationFrom.PostalCode);
        const ccf = encodeURIComponent(this.locationFrom.CountryCode);
        locationArr.push(`lf=${lf}`);
        locationArr.push(`ccf=${ccf}`);
      }
      if (this.locationTo && this.locationTo.CityAlias) {
        const postalCode =
          this.locationTo.PostalCode || this.locationTo.autoPostal;
        const lt = encodeURIComponent(postalCode);
        const cct = encodeURIComponent(this.locationFrom.CountryCode);
        locationArr.push(`lt=${lt}`);
        locationArr.push(`cct=${cct}`);
      }
      return `${languagePath}/calculator/?${locationArr.join('&')}`;
    },
  },
  methods: {
    ...mapActions('preparation', [
      'completeCalculate',
      'resetCalculation',
      'setNearestServicePoints',
      'clearNearestServicePoints',
      'setServicePointFrom',
      'setPickUpDate',
      'filterServicePointListOnCity',
    ]),
    async calculatePrices() {
      const { dispatch } = this.$store;
      const piecesNonDoc = this.pieces.filter(({ type }) => type.alias === "NonDocuments");

      if(this.locationTo.CountryCode !== 'RU' && piecesNonDoc.length > 0) {
        this.$refs.info.open();
      }

      this.$statisticService.sendCheckoutStep1();

      try {
        this.isCalculated = true;
        this.clearNearestServicePoints();
        const nearestServicePoints = await this.$api.getServicePoints({
          locationFrom: this.locationFrom,
          locationTo: this.locationTo,
        });

        const [firstNearestServicePoint] = nearestServicePoints;

        this.setNearestServicePoints(nearestServicePoints);

        if (firstNearestServicePoint && !(firstNearestServicePoint.city !== 'Адлер' || firstNearestServicePoint.city !== 'Сочи')) {
          await this.filterServicePointListOnCity({
            cityName: firstNearestServicePoint.city,
          });
        }

        this.setServicePointFrom(firstNearestServicePoint);

        await this.setPickUpDate(new Date());
        await this.$availableDateService.getWeekend();
        await this.$availableDateService.getFirstAvailableTime();
        const data = await this.$calculateService.calculatePrices();

        this.$api.getServicePoints({}).then((servicePoints) => {
          dispatch('preparation/setServicePoints', servicePoints);
        });

        this.completeCalculate(data);
        this.$nextTick(() => {
          this.$scrollTo('#result-block');
        });
      } catch (e) {
        this.resetCalculation();
      } finally {
        this.isCalculated = false;
      }

      let { __GetI } = window;
      const { __GetI_domain } = window;

      if (typeof __GetI === 'undefined') {
        __GetI = [];
      }
      const func = () => {
        const p = {
          type: 'CONVERSION',
          /* config START */
          site_id: '7134',
          order: [],
          transaction_id: '',
          revenue: '',
          pixel_id: 'tarif_В2C',
          /* config END */
        };
        __GetI.push(p);
        const domain =
          typeof __GetI_domain === 'undefined'
            ? 'px.adhigh.net'
            : __GetI_domain;
        const src = `${(document.location.protocol == 'https:'
          ? 'https://'
          : 'http://') + domain}/p.js`;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;
        const s = document.getElementsByTagName('script')[0];
        s.parentNode.insertBefore(script, s);
      };

      func();
    },
    countryToCheckError() {
      this.countryToErrorMessage = '';
      const locationToCountry = this.$refs.locationTo.selected.country;

      if (!locationToCountry) {
        return;
      }

      switch (locationToCountry.CountryCode) {
        case 'RU': this.countryToErrorMessage = this.texts.CountryToRUErrorMessage; break;
        case 'BY': this.countryToErrorMessage = this.texts.CountryToBYErrorMessage; break;
        case 'UA': this.countryToErrorMessage = this.texts.CountryToUAErrorMessage; break;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.calculate-block {
  position: relative;

  .button-icon {
    max-width: 392px;
    margin: 0 auto 60px;
  }
}

.calc-page_list {
  font-size: 20px;
  font-weight: 300;
  color: #df0715;
  padding-left: 20px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  &-wrap {
    display: flex;
    justify-content: center;
  }

  &-text {
    color: #111;
    display: inline;
  }
}

.calc-page__link-main-calc {
  width: 415px;
  margin: auto;
}

.spinner {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.6);
}

.delivery-abroad {
  text-align: center;
  margin: 10px;
}

.delivery-abroad__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  max-width: 100%;
  border-radius: 4px;
  height: 44px;
  letter-spacing: 1.2px;
  font-size: 13px;
  font-weight: 500;
  border: 2px solid #d40411;
  color: #d40411;
  text-shadow: none;
  text-decoration: none;
  text-transform: uppercase;
  transition: color 0.2s ease-in-out, background 0.2s ease-in-out;
}

.delivery-abroad__link:hover {
  background: #d40411;
  color: #ffffff;
  transition: color 0.2s ease-in-out, background 0.2s ease-in-out;
}

.link {
    color: inherit;
    font-size: inherit;
    text-decoration: none;
}
</style>
